import { useEffect, useRef, useState } from "react";
import {
  EyeOffIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  XIcon,
} from "lucide-react";

import { type SearchReviewCandidate } from "../searchV2CandidateResult";
import { SearchSheet } from "./SearchSheet";

import { Column, Row, Text } from "@/components";
import { ApplicationReviewModalActions } from "@/components/application-review/ApplicationReviewModalActions";
import { JobOverview } from "@/components/companies/jobs/JobOverview";
import { SheetNavigation } from "@/components/SheetNavigation";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { useCreateCandidacy } from "@/hooks/mutations/useCreateCandidacy";
import { useGetJobInternalConfig } from "@/hooks/queries/useGetJobInternalConfig";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { storage } from "@/lib/storage";
import { isUserTyping } from "@/lib/utils";
import {
  CandidacySource,
  CandidacyStatus,
  Job,
  ReviewDecision,
  SallyState,
  SessionShortlist,
} from "@/types";

// An interest without a search result behind it (made from the dashboard, or
// before this list saw them) carries no candidacy data — fetch it inline.
const isIdsOnlyCandidate = (candidate: SearchReviewCandidate) =>
  candidate.lastCommunication === undefined;

export const SearchApplicationReviewModal = ({
  candidates,
  companyName,
  includeCandidacyHydration = false,
  job,
  onHide,
  onOpenChange,
  onShortlisted,
  open,
}: {
  candidates: SearchReviewCandidate[];
  companyName: string;
  // For callers passing ids-only candidates (no hydrated search result) —
  // the sheet fetches the candidacy payload inline instead.
  includeCandidacyHydration?: boolean;
  job: Job;
  onHide: (talentId: number) => void;
  onOpenChange: (open: boolean) => void;
  onShortlisted: (talentId: number, shortlist: SessionShortlist) => void;
  open: boolean;
}) => {
  /* Database CRUD Operations */
  // Create
  const { isPending: isShortlisting, mutate: createCandidacy } =
    useCreateCandidacy();
  // Agent state comes from the job's config, not from whether any existing
  // candidacy happens to have the agent running.
  const { data: jobInternalConfig } = useGetJobInternalConfig();
  const isAgentActive = !!jobInternalConfig?.stafferAgentActive;

  /* React Hooks */
  // Opening the review marks the job's search as viewed — shared state read
  // by the dashboard pill and the job page's tab chip.
  useEffect(() => {
    storage.setSearchReviewViewed(job.id);
  }, [job.id]);

  const [queue, setQueue] = useState(candidates);
  const [index, setIndex] = useState(0);
  const [isJobOverviewCollapsed, setIsJobOverviewCollapsed] = useState(false);

  const safeIndex = Math.min(index, Math.max(queue.length - 1, 0));
  const candidate = queue[safeIndex];
  // When a card leaves, focus falls to the next focusable thing — the new
  // sheet's resume iframe — and its document swallows the keyboard. Pull focus
  // back to the dialog so the shortcuts keep landing.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.focus();
  }, [candidate]);

  // The listener is attached once and reads the latest handler through a ref.
  // Re-attaching on every render would remove it mid-dispatch whenever an
  // earlier capture listener forces a synchronous React commit, and a listener
  // removed during dispatch never fires for that key.
  const handleHideShortcutRef = useRef<() => void>(undefined);
  handleHideShortcutRef.current = () => {
    onHide(candidate.talentId);
    handleCandidateActioned();
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isUserTyping() || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (event.key.toLowerCase() !== "r") return;
      event.preventDefault();
      handleHideShortcutRef.current?.();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  /* Handlers */
  const handleCandidateActioned = () => {
    const nextQueue = queue.filter(
      (queueCandidate) => queueCandidate.talentId !== candidate.talentId,
    );
    if (!nextQueue.length) {
      onOpenChange(false);
      return;
    }
    setQueue(nextQueue);
    setIndex(Math.min(safeIndex, nextQueue.length - 1));
  };

  const handleHide = () => {
    onHide(candidate.talentId);
    handleCandidateActioned();
  };

  const handleShortlist = (decision: ReviewDecision) => {
    // Same do-not-contact rule as the list: shortlisting someone who opted out
    // is allowed, handing them to the agent is not.
    createCandidacy(
      {
        companyId: job.companyId,
        createCandidacyRequest: {
          agentShouldReachOutIfEnabled:
            decision === ReviewDecision.SHORTLIST &&
            candidate.sallyState !== SallyState.OPTED_OUT,
          searchAnalytics: {
            source: "search_review_modal",
          },
          source: CandidacySource.LABORUPSEARCH,
          status: CandidacyStatus.SHORTLIST,
          talentId: candidate.talentId,
        },
        currentJobId: job.id,
      },
      {
        onSuccess: (createdCandidacy) => {
          onShortlisted(candidate.talentId, {
            candidateName: [candidate.firstName, candidate.lastName]
              .filter(Boolean)
              .join(" "),
            coinsAwarded: createdCandidacy.stafferPetAward?.coinsAwarded ?? 0,
            goalBonusCoins:
              createdCandidacy.stafferPetGoalAward?.coinsAwarded ?? 0,
          });
          handleCandidateActioned();
        },
      },
    );
  };

  /* Render */
  const renderJobOverview = () => {
    if (isJobOverviewCollapsed) return null;

    return (
      <Column className="min-h-0 min-w-0 flex-[3] gap-2">
        <Row className="h-10 min-w-0 shrink-0 items-center gap-2">
          <Label className="truncate text-lg font-semibold tracking-tight text-slate-900">
            {companyName}
          </Label>
          <Text className="shrink-0 text-lg font-normal text-slate-400">·</Text>
          <Label className="truncate text-lg font-semibold tracking-tight text-slate-900">
            {job.title}
          </Label>
        </Row>
        <Column className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <JobOverview
            companyName={companyName}
            headerAction={
              <Button
                className="h-8 w-8 text-slate-500"
                onClick={() => {
                  setIsJobOverviewCollapsed(true);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <PanelLeftCloseIcon className="h-4 w-4" />
              </Button>
            }
            hideActions
            job={job}
            screenName={MIXPANEL_SCREENS.CANDIDATE_SHEET}
          />
        </Column>
      </Column>
    );
  };

  const renderCandidate = () => {
    return (
      <Column className="min-h-0 min-w-0 flex-[5] gap-2">
        <Row className="h-10 shrink-0 items-center justify-between gap-2">
          <Label className="text-lg font-semibold tracking-tight text-slate-900">
            Candidate
          </Label>
          <SheetNavigation
            betweenButtons={
              <Text className="text-xs font-medium tabular-nums text-slate-600">
                {safeIndex + 1} / {queue.length}
              </Text>
            }
            canMoveToNext={safeIndex < queue.length - 1}
            canMoveToPrevious={safeIndex > 0}
            handleMoveToNext={() => {
              setIndex(safeIndex + 1);
            }}
            handleMoveToPrevious={() => {
              setIndex(safeIndex - 1);
            }}
            screenName={MIXPANEL_SCREENS.CANDIDATE_SHEET}
            variant="outline"
          />
        </Row>
        <Column className="min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <Column className="shrink-0 px-6 pt-5">
            <ApplicationReviewModalActions
              isAgentActive={isAgentActive}
              isShortlistDisabled={
                isShortlisting ||
                candidate.candidacyStatus === CandidacyStatus.SHORTLIST
              }
              onAction={(decision) => {
                if (decision !== ReviewDecision.REJECT) {
                  handleShortlist(decision);
                }
              }}
              rejectControl={
                <Button
                  className="h-9 gap-1.5 rounded-full border-rose-200 px-4 text-sm font-semibold text-rose-700 transition-transform duration-150 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800 active:scale-95"
                  onClick={handleHide}
                  size="sm"
                  type="button"
                  variant="outlineDestructive"
                >
                  <EyeOffIcon className="h-4 w-4" />
                  Hide
                  <kbd className="pointer-events-none ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded bg-rose-100 px-1 font-mono text-[10px] font-semibold text-rose-800">
                    R
                  </kbd>
                </Button>
              }
            />
          </Column>
          <SearchSheet
            candidate={candidate}
            includeCandidacyHydration={
              includeCandidacyHydration || isIdsOnlyCandidate(candidate)
            }
            isAgentActive={isAgentActive}
            isEmbedded
            isShortlisted={
              candidate.candidacyStatus === CandidacyStatus.SHORTLIST
            }
            isShortlisting={isShortlisting}
            jobId={job.id}
            onClose={() => {
              onOpenChange(false);
            }}
            onShortlist={handleShortlist}
          />
        </Column>
      </Column>
    );
  };

  const render = () => {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent
          className="flex h-[95vh] w-[92vw] max-w-none flex-col gap-0 overflow-hidden border border-slate-200 bg-slate-100 p-0 shadow-xl sm:rounded-xl"
          hideCloseButton
          ref={contentRef}
        >
          <DialogTitle className="sr-only">Search Review Mode</DialogTitle>
          <Row className="shrink-0 items-center justify-between gap-3 border-b border-slate-200/80 bg-slate-100 px-4 py-2.5">
            <Row className="items-center gap-1.5">
              {isJobOverviewCollapsed ? (
                <Button
                  className="w-8 text-slate-500"
                  onClick={() => {
                    setIsJobOverviewCollapsed(false);
                  }}
                  type="button"
                  variant="ghost"
                >
                  <PanelLeftOpenIcon />
                </Button>
              ) : null}
              <Text className="text-sm font-medium text-slate-500">
                Search Application Review
              </Text>
            </Row>
            <Button
              className="h-8 w-8 text-slate-500"
              onClick={() => {
                onOpenChange(false);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </Row>
          <Row className="relative min-h-0 flex-1 gap-3 overflow-hidden p-3 pt-2">
            {renderJobOverview()}
            {renderCandidate()}
          </Row>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
